import React, { useState, useEffect } from 'react';
import { fetchStoneDetails } from '../utils/api';
import './StoneDetailModal.css';

const StoneDetailModal = ({ stone, isOpen, isAuthenticated, onClose, onRecordVisit }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Load richer details only when open + stone present
  useEffect(() => {
    let alive = true;
    
    // Early exit conditions
    if (!isOpen || !stone || !isAuthenticated) {
      return () => { alive = false; };
    }

    // Don't call API if we already know this is a family stone and user can't access it
    const stoneName = stone.name || '';
    const isPrivateFamilyStone = stoneName === 'Private Family Stone';

    if (isPrivateFamilyStone) {
      // Skip API call, set error to trigger family-only message
      setError('This is a private, family-only stone');
      setLoading(false);
      return () => { alive = false; };
    }

    // Make the API call
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchStoneDetails(stone.id);
        if (alive) setDetails(data);
      } catch (err) {
        if (alive) setError(err?.message || 'Failed to load stone details');
        // Don't log expected 403 errors
        if (!err?.message?.includes('family-only')) {
          console.error('Failed to load stone details:', err);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [isOpen, stone, isAuthenticated]);

  if (!isOpen || !stone) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleMediaClick = (media) => setSelectedMedia(media);
  const closeMediaViewer = () => setSelectedMedia(null);

  // Show loading state for authenticated users
  if (isAuthenticated && loading) {
    return (
      <div className="stone-modal-overlay" onClick={handleOverlayClick}>
        <div className="stone-modal-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <h2>{stone.name || 'Junior Stone'}</h2>
          <div className="modal-loading-state">
            <div className="spinner"></div>
            <p>Loading stone details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a family-only stone
  const isFamilyOnly = error?.includes('family-only') || stone.accessLevel === 'family';

  // Show family-only message
  if (isAuthenticated && error && isFamilyOnly) {
    return (
      <div className="stone-modal-overlay" onClick={handleOverlayClick}>
        <div className="stone-modal-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="family-only-message">
            <h2>Private Stone</h2>
            <p>
              This is a private, family-only stone. Only immediate family
              members can view its details and location.
            </p>
            <button onClick={onClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Show not authenticated message
  if (!isAuthenticated) {
    return (
      <div className="stone-modal-overlay" onClick={handleOverlayClick}>
        <div className="stone-modal-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="family-only-message">
            <h2>Junior Stone</h2>
            <p>There is a Junior Stone in this area--please request access to see the precise location, share your memories, and see what others have shared.</p>
            <button onClick={onClose} className="close-button">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // Show general error
  if (isAuthenticated && error && !isFamilyOnly) {
    return (
      <div className="stone-modal-overlay" onClick={handleOverlayClick}>
        <div className="stone-modal-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="modal-error">{error}</div>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    );
  }

  // Safe field derivation (only used after loading completes)
  const d = details?.stone || {};
  const name = d.name ?? stone.name ?? 'Untitled location';
  const description = d.description ?? stone.description ?? '';
  const placedBy = d.placedBy ?? stone.placedBy ?? 'Unknown';
  const placedDate = (d.placedDate ?? stone.placedDate)
    ? new Date(d.placedDate ?? stone.placedDate).toLocaleDateString()
    : '—';

  const inPersonVisitCount = details?.inPersonVisitCount ?? 0;
  const visits = details?.visits ?? [];
  const canRecordVisit = details?.userCanVisit === true;

  // Main content (after loading)
  return (
    <>
      <div className="stone-modal-overlay" onClick={handleOverlayClick}>
        <div className="stone-modal-content">
          <button className="modal-close" onClick={onClose}>×</button>

          <h2>{name}</h2>

          <div className="stone-info">
            {description && (
              <p className="stone-description">{description}</p>
            )}

            <div className="stone-meta">
              <div className="meta-item">
                <strong>Placed by:</strong> {placedBy}
              </div>
              <div className="meta-item">
                <strong>Placed on:</strong> {placedDate}
              </div>
              <div className="meta-item">
                <strong>In-person visits:</strong> {inPersonVisitCount}
              </div>
            </div>
          </div>

          {canRecordVisit && (
            <button
              className="record-visit-button"
              onClick={() => {
                onClose();
                onRecordVisit?.(stone);
              }}
            >
              Record a Visit
            </button>
          )}

          <div className="visits-section">
            <h3>Visits & Memories ({visits.length})</h3>

            {visits.length === 0 && (
              <p className="no-visits">No visits recorded yet. Be the first!</p>
            )}

            {visits.map((visit) => (
              <div key={visit.visitId} className="visit-card">
                <div className="visit-header">
                  <span className="visitor-name">{visit.userName}</span>
                  <span className="visit-date">
                    {new Date(visit.visitDate).toLocaleDateString()}
                  </span>
                  {visit.visitType === 'in-person' && (
                    <span className="visit-badge">In-Person</span>
                  )}
                </div>

                {visit.memoryText && (
                  <p className="visit-memory">{visit.memoryText}</p>
                )}

                {visit.mediaFiles && visit.mediaFiles.length > 0 && (
                  <div className="visit-media">
                    {visit.mediaFiles.map((media, index) => {
                      // Derive type from fileType if type is missing (for backward compatibility)
                      const mediaType = media.type || (media.fileType?.startsWith('image/') ? 'image' : 'video');
                      
                      return (
                        <div
                          key={index}
                          className="media-thumbnail"
                          onClick={() => handleMediaClick({ ...media, type: mediaType })}
                        >
                          {mediaType === 'image' && (
                            <img src={media.url} alt="Visit memory" />
                          )}
                          {mediaType === 'video' && (
                            <div className="video-thumbnail">
                              <video src={media.url} />
                              <div className="play-icon">▶</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedMedia && (
        <div className="media-viewer-overlay" onClick={closeMediaViewer}>
          <button className="media-close" onClick={closeMediaViewer}>×</button>
          <div className="media-viewer-content" onClick={(e) => e.stopPropagation()}>
            {selectedMedia.type === 'image' && (
              <img src={selectedMedia.url} alt="Full size" />
            )}
            {selectedMedia.type === 'video' && (
              <video src={selectedMedia.url} controls autoPlay />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StoneDetailModal;